import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
// Import the CSS
import '../index.css';
import Loader from '../components/LoaderComponent';
import { HelmetProvider } from 'react-helmet-async';
import { Helmet } from 'react-helmet';
import FAQFamily from '../components/HolidayFAQs/FamilyFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/FamilySlider";
import Slider2 from "../components/CountrySliders/Mauritius/FamilySlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

// Lazy load the components
// const LazyLatestDestinationCard = React.lazy(() => import('../components/LastestDestinationCard'));
const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/Family'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageFamily = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  // const [isExpanded, setIsExpanded] = useState(false);
  // const toggleText = () => setIsExpanded(!isExpanded);


  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Family Holidays | Kid-Friendly Resorts & Activities | Halo Holidays</title>
          <meta name="description" content="Create unforgettable memories with your loved ones on our family holidays. Explore diverse destinations, enjoy kid-friendly resorts, and embark on exciting adventures." />
          {/* <meta name="keywords" content="travel, hotels, holidays, deals, holiday packages" /> */}
        </Helmet>
      </HelmetProvider>

      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">Family Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              Family holidays are the ultimate bonding experience, a chance to explore new worlds together and create memories that last a lifetime. Whether your brood is filled with tiny tots or adventurous teens, we've got family holiday packages tailored to every age and interest. Fancy building sandcastles on sun-kissed beaches in Spain, snorkelling through crystal-clear waters in Thailand, or embarking on a wildlife safari during your holiday to <a href='https://www.haloholidays.co.uk/destinations/sri-lanka' className='text-halo-pink'>Sri Lanka</a>? The world is your oyster! Our all-inclusive family resorts take the stress out of planning, with everything from flights and accommodation to meals, drinks, and activities all bundled into one convenient price. You can relax and enjoy quality time together, knowing that every detail has been taken care of.
              <br /><br />
              If you're looking for a bit more adventure, consider our family tours designed to ignite a sense of wonder. Explore ancient ruins, trek through lush rainforests, or even learn to cook traditional dishes in a foreign land. And for those who love a spontaneous getaway, our last-minute family holiday deals offer fantastic savings on unforgettable experiences.
              <br /><br />
              Whether you're dreaming of a relaxing beach escape in the Maldives, a cultural immersion in Japan, or an action-packed adventure in Costa Rica, our carefully curated selection of destinations will cater to every family's unique interests and preferences. From kid-friendly resorts with thrilling water parks and engaging kids' clubs to educational tours that inspire curiosity and wonder, we've got something for everyone. So, gather your loved ones, pack your bags, and let Halo Holidays create a family holiday that will be cherished for years to come.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Discover Our Handpicked Family Resorts</h2>
            <p className='text-black'>
              Family resorts are the ultimate haven for creating unforgettable memories with your loved ones. These specially designed havens cater to every age and interest, offering a wide range of activities and amenities that keep both kids and adults entertained. From sprawling water parks with thrilling slides and lazy rivers to supervised kids' clubs with engaging programmes, these resorts ensure that children have a blast while parents can relax and unwind.
              <br /><br />
              Indulge in family-friendly dining experiences, explore exciting destinations, and take advantage of <a href='https://www.haloholidays.co.uk/all-inclusive-holidays' className='text-halo-pink'>all-inclusive</a> packages that offer great value for money. Whether you're dreaming of a beach holiday, an adventure in the mountains, or a cultural city break, family resorts provide the perfect setting for a memorable and stress-free holiday.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Not a Family Holiday Person? No Problem, We Have Other Options</h2>
            <p className='text-black'>
              Whether you're dreaming of a romantic getaway for two, an adventure-filled trip with friends, a solo journey of self-discovery, or perhaps even a <a href='https://www.haloholidays.co.uk/last-minute-holidays' className='text-halo-pink'>last-minute family holiday</a> to make the most of those school holidays, we've got a wide range of options to suit your needs. Our last minute holidays are perfect for those who love to seize the moment and embark on spontaneous adventures. So why wait? Explore our diverse collection of holiday types and start planning your next unforgettable experience today!
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Family" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Family" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQFamily />

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">Latest All Inclusive Destinations</h2>
      <p>Most recent all-inclusive destinations added by our hosts</p>
      </Suspense> */}
        {/* Displaying the top row hotels */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div>
       */}
        {/* Displaying the bottom row hotels */}
        {/* <div className="grid md:grid-cols-3 gap-4">
        {bottomRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}

        <Suspense fallback={<Loader />}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-4'>Related Blogs</h2>
          <BlogSliderType blogType='family_compass' />
        </section>

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">More All Inclusive Destinations</h2>
      <p>Explore more all-inclusive destinations</p>
      </Suspense> */}

        {/* Optionally, display more hotels with AllInclusiveHotelCard */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyAllInclusiveHotelCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}
      </section >
    </>
  );
};

export default PageFamily;
